<template>
  <fieldset :disabled="disabled">
    <b-card no-body>
      <b-card-body>
        <b-form novalidate>
          <fieldset class="mb-3">
            <b-form-row>
              <b-col lg="6" md="6" sm="12">
                <b-form-group label="Nome" label-for="name">
                  <b-form-input
                    id="name"
                    v-model="form.name"
                    :state="validation.name"
                  />
                  <b-form-invalid-feedback v-if="$v.form.name.$invalid">
                    Informe o nome
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="6" sm="12">
                <b-form-group label="Usuário" label-for="email">
                  <b-form-input
                    id="email"
                    v-model="form.email"
                    :state="validation.email"
                  />
                  <b-form-invalid-feedback v-if="$v.form.email.$invalid">
                    Informe um e-mail válido
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="6" sm="12">
                <b-form-group label="Senha" label-for="password">
                  <b-form-input
                    id="password"
                    v-model="form.password"
                    :state="validation.password"
                    type="password"
                  />
                  <b-form-invalid-feedback v-if="$v.form.password.$invalid">
                    Informe uma senha
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="6" sm="12">
                <b-form-group
                  label="Confirme a Senha"
                  label-for="password-confirmation"
                >
                  <b-form-input
                    id="password-confirmation"
                    v-model="form.passwordConfirmation"
                    :state="validation.passwordConfirmation"
                    type="password"
                  />
                  <b-form-invalid-feedback
                    v-if="$v.form.passwordConfirmation.$invalid"
                  >
                    <span
                      v-if="!$v.form.passwordConfirmation.required"
                      class="d-block"
                    >
                      Informe novamente a senha
                    </span>
                    <span
                      v-else-if="!$v.form.passwordConfirmation.matchesPassword"
                      class="d-block"
                    >
                      As senhas devem ser iguais
                    </span>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-form-row>
            <hr />
            <b-form-group
              label="Atualizacao de alugueis"
              label-for="rent-update-mode"
            >
              <b-form-select
                id="rent-update-mode"
                v-model="form.rentUpdateMode"
                :options="rentUpdateModeOptions"
                :state="validation.rentUpdateMode"
              />
              <b-form-invalid-feedback v-if="$v.form.rentUpdateMode.$invalid">
                Informe o modo de atualizacao
              </b-form-invalid-feedback>
            </b-form-group>
            <hr />
            <StoresFetcher>
              <template #default="{ data }">
                <fieldset>
                  <legend class="d-flex">
                    <span class="mr-3"> Lojas e Permissões </span>
                    <b-button
                      v-if="!fixedStore"
                      variant="info"
                      size="sm"
                      :disabled="isAddUserStoreDisabled(data)"
                      @click="handleAddUserStore"
                    >
                      <span class="fas fa-plus mr-1" /> Adicionar
                    </b-button>
                  </legend>

                  <div
                    v-for="(userStore, index) in form.userStores"
                    :key="userStore.storeId || `new-${index}`"
                  >
                    <UserStores
                      v-model="form.userStores[index]"
                      :fixed-store="fixedStore"
                      :stores="
                        fixedStore
                          ? getFixedStoreOptions(data, userStore.storeId)
                          : parseStoreOptions(data, userStore)
                      "
                      :roles="determineRolesForUserStore()"
                      :role-select-disabled="shouldDisableRoleSelect()"
                      :validation="$v.form.userStores"
                      :validate="$v.form.$dirty"
                      :enable-remove="
                        !fixedStore && (form.userStores.length > 1 || index > 0)
                      "
                      :disabled="fixedStore"
                      @removeUserStore="handleRemoveUserStore"
                    />
                  </div>
                  <div
                    v-if="!fixedStore && form.userStores.length > 1"
                    class="text-info small mt-1 font-weight-bold"
                  >
                    Usuarios com mais de uma loja terão a permissão de
                    <span class="text-danger">Admin Geral</span>
                    Obrigatoriamente.
                  </div>
                </fieldset>
              </template>
            </StoresFetcher>
            <hr />
            <StatusInput
              v-model="form.status"
              :validation="validation.status"
              :invalid="$v.form.status.$invalid"
            />
          </fieldset>
        </b-form>
      </b-card-body>
    </b-card>

    <div class="mt-3 d-flex gap-1 justify-content-end">
      <b-button variant="outline-secondary" @click="handleCancel('UsersIndex')">
        Cancelar
      </b-button>
      <b-button variant="success" @click="handleSubmit"> Salvar </b-button>
    </div>
  </fieldset>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { RENT_UPDATE_MODE_IDS, RENT_UPDATE_MODES, ROLES } from '@/constants' // Certifique-se que o caminho está correto
import withFormValidation from '@/mixins/withFormValidation'
import StatusInput from '@/components/StatusInput'
import StoresFetcher from '@/components/Stores/StoresFetcher'
import UserStores from '@/components/Users/UserStores'
import { isEmpty } from 'lodash/fp'

export default {
  name: 'UserForm',
  components: {
    StatusInput,
    StoresFetcher,
    UserStores
  },
  mixins: [
    withFormValidation({
      fields: [
        'userStores',
        'name',
        'email',
        'password',
        'passwordConfirmation',
        'rentUpdateMode',
        'status'
      ]
    })
  ],
  props: {
    value: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    fixedStore: {
      type: Boolean,
      default: false
    }
  },
  validations: {
    form: {
      name: { required },
      email: { required },
      password: {
        required: (value, { id }) => isEmpty(id)
      },
      passwordConfirmation: {
        required: (value, { id }) => isEmpty(id),
        matchesPassword: (value, { password }) => value === password
      },
      userStores: { required }, // Vuelidate pode precisar de validações mais complexas aqui se necessário
      rentUpdateMode: { required },
      status: { required }
    }
  },
  data() {
    return {
      roles: ROLES // Mantém ROLES disponível
    }
  },
  computed: {
    form: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    rentUpdateModeOptions() {
      return Object.values(RENT_UPDATE_MODES).map((mode) => ({
        value: mode.value,
        text: mode.text
      }))
    }
  },
  created() {
    if (!this.form.rentUpdateMode) {
      this.$set(this.form, 'rentUpdateMode', RENT_UPDATE_MODE_IDS.SSE)
    }
  },
  watch: {
    'form.userStores.length': {
      handler(newLength) {
        // Se fixedStore está ativo, esta lógica de multi-loja não se aplica,
        // pois fixedStore já dita a loja e a permissão.
        if (this.fixedStore) {
          return
        }

        if (newLength > 1) {
          this.form.userStores.forEach((userStore) => {
            // Define a permissão para Admin Geral se ainda não for
            if (userStore.role !== this.roles.ADMIN.value) {
              userStore.role = this.roles.ADMIN.value
            }
          })
        }
        // Se newLength <= 1, não revertemos a role automaticamente.
        // O select se tornará editável (se não for fixedStore) com todas as opções
        // e o usuário poderá alterar a role que ficou como ADMIN (se for o caso).
      }
      // `immediate: true` pode ser considerado se os dados iniciais carregados no formulário
      // puderem ter múltiplas lojas sem a permissão ADMIN correta.
      // immediate: true
    }
  },
  methods: {
    getFixedStoreOptions(data, storeId) {
      const store = data.find((s) => s.id === storeId)
      return store ? [{ code: store.id, label: store.name }] : []
    },
    getFixedRoleOptions() {
      // Para fixedStore, a role é definida (ex: ADMIN_FRANCHISEE ou EMPLOYER)
      // A regra de "Admin Geral" para >1 loja não se aplica aqui.
      // Supondo que fixedStore usa EMPLOYER como padrão, ou poderia ser outra:
      const franchiseeAdminRole =
        this.roles.ADMIN_FRANCHISEE || this.roles.EMPLOYER // Fallback
      return [
        { code: franchiseeAdminRole.value, label: franchiseeAdminRole.text }
      ]
    },
    getRolesOptions() {
      return Object.values(this.roles).map((role) => ({
        code: role.value,
        label: role.text
      }))
    },

    // NOVO: Determina quais opções de roles devem ser mostradas
    determineRolesForUserStore() {
      if (this.fixedStore) {
        return this.getFixedRoleOptions()
      }
      if (this.form.userStores && this.form.userStores.length > 1) {
        const adminRole = this.roles.ADMIN
        return [{ code: adminRole.value, label: adminRole.text }]
      }
      return this.getRolesOptions()
    },

    // NOVO: Determina se o select de roles deve ser desabilitado
    shouldDisableRoleSelect() {
      if (this.fixedStore) {
        // Se a loja é fixa, a role também é (e o select desabilitado)
        return true
      }
      // Se não for fixedStore, desabilita o select de role apenas se houver mais de uma loja
      return this.form.userStores && this.form.userStores.length > 1
    },

    handleAddUserStore() {
      if (this.fixedStore) return // Botão não deveria estar visível

      let initialRole = this.roles.EMPLOYER.value // Role padrão para uma nova loja única

      // Se a adição desta loja resultar em mais de uma loja, defina a role como Admin Geral
      if (this.form.userStores.length + 1 > 1) {
        initialRole = this.roles.ADMIN.value
      }

      this.form.userStores = [
        ...this.form.userStores,
        {
          storeId: null,
          role: initialRole // O watcher garantirá a consistência de todas as roles
        }
      ]
    },
    handleRemoveUserStore({ storeId }) {
      this.form.userStores = this.form.userStores.filter(
        (userStore) => userStore.storeId !== storeId
      )
      // O watcher em 'form.userStores.length' cuidará de ajustar as roles se necessário.
    },
    isAddUserStoreDisabled(stores) {
      // Impede adicionar mais lojas do que o total disponível
      if (!stores) return true
      return this.form.userStores.length >= stores.length
    },
    parseStoreOptions(stores, { storeId: currentStoreId }) {
      const userStoresIds = this.form.userStores
        .map(({ storeId }) => storeId)
        .filter((id) => id !== currentStoreId) // Exclui o ID da loja atual da lista de já selecionados

      return stores
        .filter(({ id }) => !userStoresIds.includes(id)) // Filtra lojas já selecionadas em OUTROS UserStores
        .map(({ id, name }) => ({
          code: id,
          label: name
        }))
    },
    handleCancel(routeName) {
      this.$router.push({ name: routeName })
    },
    handleSubmit() {
      this.$v.$touch()
      if (this.$v.$invalid) return
      this.$emit('submit', this.form)
    }
  }
}
</script>
